import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { socketService } from '../services/socketService';
import { notificationsAPI } from '../services/api';

interface Notification {
  id: number;
  userId: string;
  title: string;
  message: string;
  link: string | null;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { sessionToken } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  // Initialize socket once when token is available
  useEffect(() => {
    if (sessionToken) {
      socketService.initialize(sessionToken);
    }
  }, [sessionToken]);

  // Subscribe to socket events - stable callbacks that never change
  useEffect(() => {
    const handleConnect = () => {
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    const handleNotification = (notification: Notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    };

    const handleNotificationRead = (data: { notificationId: string }) => {
      // Compare as strings. Idempotent: only decrement when actually transitioning
      // unread -> read, so a socket echo of an already-optimistically-read item
      // (audit #234) doesn't double-decrement the badge.
      const idString = data.notificationId;
      setNotifications(prev => {
        const target = prev.find(n => n.id.toString() === idString);
        if (target && !target.read) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }
        return prev.map(n => n.id.toString() === idString ? { ...n, read: true } : n);
      });
    };

    const handleNotificationDeleted = (data: { notificationId: string }) => {
      // Compare as strings - notification IDs come back as strings from socket
      const idString = data.notificationId;
      setNotifications(prev => {
        const notification = prev.find(n => n.id.toString() === idString);
        if (notification && !notification.read) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }
        return prev.filter(n => n.id.toString() !== idString);
      });
    };

    const handleAllNotificationsDeleted = () => {
      setNotifications([]);
      setUnreadCount(0);
    };

    const handleAllNotificationsRead = () => {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    };

    socketService.subscribe('connect', handleConnect);
    socketService.subscribe('disconnect', handleDisconnect);
    socketService.subscribe('notification', handleNotification);
    socketService.subscribe('notification:read', handleNotificationRead);
    socketService.subscribe('notification:deleted', handleNotificationDeleted);
    socketService.subscribe('notifications:all_deleted', handleAllNotificationsDeleted);
    socketService.subscribe('notifications:all_read', handleAllNotificationsRead);

    setIsConnected(socketService.isConnected());

    return () => {
      socketService.unsubscribe('connect', handleConnect);
      socketService.unsubscribe('disconnect', handleDisconnect);
      socketService.unsubscribe('notification', handleNotification);
      socketService.unsubscribe('notification:read', handleNotificationRead);
      socketService.unsubscribe('notification:deleted', handleNotificationDeleted);
      socketService.unsubscribe('notifications:all_deleted', handleAllNotificationsDeleted);
      socketService.unsubscribe('notifications:all_read', handleAllNotificationsRead);
    };
  }, []); // Empty deps - these callbacks NEVER change

  const fetchNotifications = useCallback(async () => {
    if (!sessionToken) return;

    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [sessionToken]);

  const fetchUnreadCount = useCallback(async () => {
    if (!sessionToken) return;

    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [sessionToken]);

  const markAsRead = useCallback(async (notificationId: number) => {
    const idString = notificationId.toString();
    // Optimistic local update (audit #234): the badge/UI must reflect the action
    // even if the socket is disconnected and never echoes back. The socket handler
    // above is idempotent so a later echo won't double-count.
    setNotifications(prev => {
      const target = prev.find(n => n.id.toString() === idString);
      if (target && !target.read) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
      return prev.map(n => n.id.toString() === idString ? { ...n, read: true } : n);
    });
    // Socket is the primary channel (server updates DB and broadcasts). When it's
    // down, the emit would be a no-op, so hit the REST endpoint instead and
    // reconcile from the server if it fails (audit #234).
    if (socketService.isConnected()) {
      socketService.markAsRead(idString);
      return;
    }
    if (!sessionToken) return;
    try {
      await notificationsAPI.markRead(sessionToken, idString);
    } catch (error) {
      console.error('Failed to persist mark-as-read (REST fallback):', error);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [sessionToken, fetchNotifications, fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    // Optimistic local update (audit #234) so the badge clears even if the socket is down.
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    if (socketService.isConnected()) {
      socketService.markAllAsRead();
      return;
    }
    if (!sessionToken) return;
    try {
      await notificationsAPI.markAllRead(sessionToken);
    } catch (error) {
      console.error('Failed to persist mark-all-read (REST fallback):', error);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [sessionToken, fetchNotifications, fetchUnreadCount]);

  const deleteNotification = useCallback(async (notificationId: number) => {
    const idString = notificationId.toString();
    // Optimistic local update (audit #234): drop it now; a later socket echo won't
    // find it, so the badge count stays correct.
    setNotifications(prev => {
      const target = prev.find(n => n.id.toString() === idString);
      if (target && !target.read) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
      return prev.filter(n => n.id.toString() !== idString);
    });
    if (socketService.isConnected()) {
      socketService.deleteNotification(idString);
      return;
    }
    if (!sessionToken) return;
    try {
      await notificationsAPI.remove(sessionToken, idString);
    } catch (error) {
      console.error('Failed to persist delete (REST fallback):', error);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [sessionToken, fetchNotifications, fetchUnreadCount]);

  const deleteAllNotifications = useCallback(async () => {
    // Optimistic local update (audit #234) so the list/badge clear even if the socket is down.
    setNotifications([]);
    setUnreadCount(0);
    if (socketService.isConnected()) {
      socketService.deleteAllNotifications();
      return;
    }
    if (!sessionToken) return;
    try {
      await notificationsAPI.removeAll(sessionToken);
    } catch (error) {
      console.error('Failed to persist delete-all (REST fallback):', error);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [sessionToken, fetchNotifications, fetchUnreadCount]);

  // Fetch initial data when token is available
  useEffect(() => {
    if (sessionToken) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [sessionToken, fetchNotifications, fetchUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
